/**
 * User Details Modal
 * Shows detailed usage information for a specific user
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { X, User, Activity, Package } from 'lucide-react';
import '../AIGarage.css';

const UserDetailsModal = ({ isOpen, user, onClose }) => {
  if (!isOpen || !user) return null;

  const getUserInitials = (email) => {
    if (!email) return '?';
    const name = email.split('@')[0];
    const parts = name.split('.');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserColor = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="garage-modal-header">
          <div>
            <h2 className="garage-modal-title">User Activity</h2>
          </div>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="garage-modal-body">
          {/* User Info */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            padding: '20px',
            backgroundColor: 'var(--garage-bg-2)',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: getUserColor(),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: '600',
                fontSize: '24px'
              }}
            >
              {getUserInitials(user.userEmail)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '4px' }}>
                {user.userName}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--garage-text-3)' }}>
                {user.userEmail}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--garage-bg-2)',
              borderRadius: '8px',
              borderLeft: '4px solid var(--garage-blue)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Activity size={18} style={{ color: 'var(--garage-blue)' }} />
                <span style={{ fontSize: '12px', color: 'var(--garage-text-3)', textTransform: 'uppercase', fontWeight: '600' }}>
                  Sessions
                </span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--garage-text)' }}>
                {user.sessions}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--garage-text-3)', marginTop: '4px' }}>
                Total tool launches
              </div>
            </div>

            <div style={{
              padding: '20px',
              backgroundColor: 'var(--garage-bg-2)',
              borderRadius: '8px',
              borderLeft: '4px solid var(--garage-orange)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Package size={18} style={{ color: 'var(--garage-orange)' }} />
                <span style={{ fontSize: '12px', color: 'var(--garage-text-3)', textTransform: 'uppercase', fontWeight: '600' }}>
                  Tools Used
                </span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--garage-text)' }}>
                {user.toolsUsed || 0}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--garage-text-3)', marginTop: '4px' }}>
                Different tools accessed
              </div>
            </div>
          </div>

          {/* Average Sessions per Tool */}
          {user.toolsUsed > 0 && (
            <div style={{
              padding: '16px',
              backgroundColor: 'var(--garage-bg-2)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--garage-text-3)', marginBottom: '8px' }}>
                Average Sessions per Tool
              </div>
              <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--garage-green)' }}>
                {(user.sessions / user.toolsUsed).toFixed(1)}
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div style={{ 
            marginTop: '24px', 
            padding: '12px', 
            backgroundColor: 'var(--garage-bg-2)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--garage-text-3)',
            textAlign: 'center'
          }}>
            💡 Detailed session history and tool-specific analytics coming soon
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default UserDetailsModal;
